<template>
  <v-dialog v-model="dialog" max-width="800">
    <v-card>
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t('labels.details') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <div class="mb-4">
          <div class="text-subtitle-2">
            {{ previewContext?.fileName || '' }}
          </div>
          <div class="text-body-2 text-grey">
            {{ previewContext?.title || '' }}
          </div>
        </div>

        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-linear>

        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
          {{ error }}
        </v-alert>

        <div v-if="!loading && previewData">
          <v-row dense class="mb-2 text-center">
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.column-count') }}</div>
              <div class="text-subtitle-1">{{ previewData.column_count ?? '—' }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.row-count') }}</div>
              <div class="text-subtitle-1">{{ previewData.row_count ?? '—' }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.created-at') }}</div>
              <div class="text-subtitle-1">{{ formatDate(previewContext?.created) }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.updated-at') }}</div>
              <div class="text-subtitle-1">{{ formatDate(previewContext?.modified) }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.format') }}</div>
              <div class="text-subtitle-1">{{ file?.format || '—' }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="text-caption text-grey">{{ $t('labels.size') }}</div>
              <div class="text-subtitle-1">{{ formatFileSize(file?.file_size) }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <div class="text-overline mb-2 text-center text-primary">{{ $t('labels.columns') }}</div>
          <v-table density="compact">
            <thead>
              <tr>
                <th class="text-left">{{ $t('labels.column-name') }}</th>
                <th class="text-left">{{ $t('labels.column-type') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(col, idx) in previewData.columns || []" :key="idx">
                <td>{{ col.name }}</td>
                <td>{{ col.type }}</td>
              </tr>
            </tbody>
          </v-table>

          <v-alert
            v-if="previewData.errors && previewData.errors.length > 0"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            <div class="mb-2">{{ $t('labels.errors') }}</div>
            <v-list density="compact">
              <v-list-item v-for="(err, idx) in previewData.errors" :key="idx">
                <v-list-item-title>{{ err }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-alert>
        </div>

        <v-alert v-else-if="!loading && !previewData && !error" type="info" variant="tonal">
          {{ $t('strings.no-details-available') }}
        </v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          v-if="!standalone"
          color="secondary"
          variant="text"
          prepend-icon="mdi-file-plus"
          :disabled="!file"
          @click="handleAdd"
        >
          {{ $t('labels.add') }}
        </v-btn>
        <v-btn
          color="secondary"
          variant="text"
          prepend-icon="mdi-file-download"
          :disabled="!file"
          @click="handleDownload"
        >
          {{ $t('labels.download') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="close">
          {{ $t('labels.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  previewData: {
    type: Object,
    default: null,
  },
  previewContext: {
    type: Object,
    default: null,
  },
  file: {
    type: Object,
    default: null,
  },
  standalone: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'add', 'download'])

const { locale } = useI18n()

const dialog = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

function close() {
  dialog.value = false
}

function handleAdd() {
  emit('add', props.file)
}

function handleDownload() {
  emit('download', props.file)
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale.value || 'en', {
    dateStyle: 'medium',
  }).format(date)
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '—'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}
</script>
